import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ROLE_LABELS, can, type RoleCode } from '@rat/shared';
import { useAuth } from '../auth/AuthProvider';
import { ThemeToggle } from './ThemeToggle';
import { AppMark } from './Logo';

/**
 * Sin selector de rol (D13): el rol y la vista se derivan del usuario autenticado.
 * Recortado respecto al AppShell real: los enlaces a auditoria, reporte,
 * importacion, reportes de bugs y administracion se retiraron porque esas
 * paginas no existen en este slice del backend -- ver router.tsx. El boton
 * de bug report tambien se retiro (dependia de una Edge Function de Supabase).
 *
 * Tablero institucional, tablero de unidad, seguimiento del levantamiento,
 * catalogos, usuarios y auditoria SI se reconectaron: el backend ya los
 * sostiene (dashboards.ts, tracking.ts, catalog-admin.ts, admin.ts,
 * audit.ts). "Catálogos" es nuevo de verdad -- ni el sistema original tuvo
 * esa pantalla. "Usuarios" existia como backend puro
 * (setInstitutional/assignUserToUnit, con su anti-autoelevacion ya
 * probada) pero sin rutas HTTP ni UI hasta esa pasada. "Auditoría" existia
 * solo como /audit/verify (un veredicto, sin lista de eventos) hasta esta
 * pasada (ver README del backend).
 * El ciclo de revision del DPD (observaciones, aprobar/observar) no tiene
 * enlace propio -- vive dentro de ActivityDetail cuando la actividad esta
 * EN_REVISION, no hace falta una "bandeja" separada porque ActivitiesList ya
 * filtra por estado.
 */
export function AppShell() {
  const { authz, session, signOut } = useAuth();
  const roleLabel = deriveRoleLabel(authz);
  const myUnitId = (authz.units.find((u) => u.unit_role === 'jefe') ?? authz.units[0])?.unit_id;
  const loc = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => setMenuOpen(false), [loc.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className="shell">
      <div
        className={`sidebar-scrim${menuOpen ? ' is-open' : ''}`}
        onClick={closeMenu}
        aria-hidden
      />

      <aside className={`sidebar${menuOpen ? ' is-open' : ''}`} id="nav-cajon">
        <div className="brand">
          <AppMark />
        </div>

        <div className="grp">RAT</div>
        <NavLink to="/actividades" className={navcls} onClick={closeMenu}>Actividades</NavLink>
        {(can(authz, 'institutional.view') || can(authz, 'unit.read.all')) && (
          <NavLink to="/institucional" className={navcls} onClick={closeMenu}>Tablero institucional</NavLink>
        )}
        {(can(authz, 'institutional.view') || can(authz, 'unit.read.all')) && (
          <NavLink to="/reportes" className={navcls} onClick={closeMenu}>Reportes</NavLink>
        )}
        {myUnitId && (
          <NavLink to={`/unidad/${myUnitId}`} className={navcls} onClick={closeMenu}>Mi unidad</NavLink>
        )}
        <NavLink to="/seguimiento" className={navcls} onClick={closeMenu}>Seguimiento</NavLink>
        {can(authz, 'institutional.view') && (
          <NavLink to="/catalogos" className={navcls} onClick={closeMenu}>Catálogos</NavLink>
        )}
        {can(authz, 'institutional.view') && (
          <NavLink to="/usuarios" className={navcls} onClick={closeMenu}>Usuarios</NavLink>
        )}
        {can(authz, 'institutional.view') && (
          <NavLink to="/bitacora" className={navcls} onClick={closeMenu}>Auditoría</NavLink>
        )}

        <div className="sidebar-foot">
          <span className="grp" style={{ padding: '0 0 6px' }}>Tema</span>
          <ThemeToggle />
        </div>
      </aside>

      <div>
        <div className="topbar">
          <button
            type="button"
            className="navtoggle"
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
            aria-controls="nav-cajon"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
          <span className="rolebadge">{roleLabel}</span>
          <div className="spacer" />
          <ThemeToggle />
          <span className="topbar-email mono">{session?.user.email}</span>
          <button className="btn btn--ghost" onClick={() => void signOut()}>
            Cerrar sesión
          </button>
        </div>
        <main className="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const navcls = ({ isActive }: { isActive: boolean }) => (isActive ? 'active' : undefined);

function deriveRoleLabel(authz: ReturnType<typeof useAuth>['authz']): string {
  const p = new Set(authz.permissions);
  const order: Array<[RoleCode, () => boolean]> = [
    ['dpd_admin', () => p.has('activity.review') && p.has('institutional.view')],
    ['auditor', () => p.has('unit.read.all') && !p.has('institutional.view')],
    ['unit_manager', () => authz.units.some((u) => u.unit_role === 'jefe')],
    ['collaborator', () => true],
  ];
  for (const [code, test] of order) if (test()) return ROLE_LABELS[code];
  return 'Usuario';
}
